import React, { useState, useEffect } from 'react';
import { formatMoney } from './PortfolioComponents';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { 
    ArrowUpRight, ArrowDownRight, Wallet, PieChart as PieIcon, 
    Calendar, DollarSign, Activity, RefreshCw, Layers, TrendingDown,
    Zap, Target, ShieldCheck, Clock, BellRing, Award
} from 'lucide-react';
import { api } from '../services/apiClient';
import TridentOpportunityWidget from '../components/dashboard/TridentOpportunityWidget';
import { useStockModal } from '../context/StockModalContext';
import { useTranslation } from 'react-i18next';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#84cc16', '#ef4444', '#64748b'];

const EarningsCalendarWidget = ({ earnings }) => {
    if (!earnings || earnings.length === 0) return null;
    
    const displayEarnings = earnings.slice(0, 3); 
    
    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 flex flex-col h-full min-h-[220px]">
            <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-slate-50 text-slate-600 rounded-xl shadow-inner border border-slate-100">
                    <Calendar size={18}/>
                </div>
                <h3 className="font-black text-slate-800 text-sm uppercase tracking-wider">Résultats à venir</h3>
            </div>
            <div className="space-y-3 flex-1 flex flex-col justify-center">
                {displayEarnings.map((e, i) => (
                    <div key={i} className="flex justify-between items-center p-3 hover:bg-slate-50 rounded-2xl transition-colors cursor-default border border-transparent hover:border-slate-100">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-black text-sm text-slate-600 shadow-sm">
                                {e.symbol.charAt(0)}
                            </div>
                            <span className="font-black text-slate-900">{e.symbol}</span>
                        </div>
                        <div className="text-right">
                            <div className="font-bold text-slate-700 text-xs mb-1">{new Date(e.date).toLocaleDateString()}</div>
                            <div className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider bg-indigo-50 px-2 py-0.5 rounded-lg inline-block">
                                Est: {e.epsEstimated !== null ? `$${e.epsEstimated.toFixed(2)}` : "N/A"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function DashboardPage() {
    const { t, i18n } = useTranslation();
    const { openStockModal } = useStockModal();
    const [portfolios, setPortfolios] = useState([]);
    const [selectedPortfolio, setSelectedPortfolio] = useState(null);
    
    const [summary, setSummary] = useState(null);
    const [taxonomy, setTaxonomy] = useState(null);
    const [projection, setProjection] = useState(null);
    
    const [radarAlerts, setRadarAlerts] = useState([]);
    const [earningsData, setEarningsData] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [greeting, setGreeting] = useState('');
    const [userName, setUserName] = useState('');

    useEffect(() => {
        const hour = new Date().getHours();
        if (hour < 12) setGreeting(t('dashboard.greeting_morning'));
        else if (hour < 18) setGreeting(t('dashboard.greeting_afternoon'));
        else setGreeting(t('dashboard.greeting_evening'));
    }, [t]);

    useEffect(() => {
        api.get('/auth/me').then(res => {
            const name = res.data.first_name || res.data.username || res.data.name;
            if (name) setUserName(name.charAt(0).toUpperCase() + name.slice(1));
        }).catch(err => console.error("Erreur profil:", err));

        api.get('/portfolio/').then(r => {
            setPortfolios(r.data);
            if (r.data.length > 0 && !selectedPortfolio) setSelectedPortfolio(r.data[0].id);
        }).catch(e => {
            console.error(e);
            setLoading(false);
        });
    }, []);

    const loadData = async () => {
        if (!selectedPortfolio) return;
        setLoading(true);
        try {
            // SUPPRESSION DE L'APPEL /charts POUR ACCÉLÉRER LE CHARGEMENT
            const [sumRes, taxRes, projRes] = await Promise.all([
                api.get(`/portfolio/${selectedPortfolio}/summary`),
                api.get(`/portfolio/${selectedPortfolio}/taxonomy`),
                api.get(`/portfolio/${selectedPortfolio}/dividends/projection`)
            ]);
            
            setSummary(sumRes.data);
            setTaxonomy(taxRes.data);
            setProjection(projRes.data);

            if (sumRes.data.positions && sumRes.data.positions.length > 0) {
                api.get(`/portfolio/${selectedPortfolio}/radar`)
                   .then(res => { if (res.data) setRadarAlerts(res.data.filter(i => (i.rsi && i.rsi < 30) || (i.rsi && i.rsi > 70))); })
                   .catch(err => console.error(err));
                    
                api.get(`/portfolio/${selectedPortfolio}/earnings-calendar`)
                   .then(res => { if(res.data) setEarningsData(res.data); })
                   .catch(err => { console.error(err); setEarningsData([]); });
            }
        } catch (e) { 
            console.error("Erreur Dashboard:", e); 
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [selectedPortfolio]);

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const currentMonthDivs = summary?.dividends?.filter(d => {
        const date = new Date(d.date);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    }).reduce((acc, curr) => acc + curr.amount, 0) || 0;

    const positions = summary?.positions || [];
    const topWeight = [...positions].sort((a, b) => b.weight - a.weight).slice(0, 4);
    const bestPerf = [...positions].filter(p => p.gain_p > 0).sort((a, b) => b.gain_p - a.gain_p).slice(0, 4);
    const worstPerf = [...positions].filter(p => p.gain_p < 0).sort((a, b) => a.gain_p - b.gain_p).slice(0, 4);

    const getSectorData = () => {
        if (!taxonomy?.sectors) return [];
        const sorted = [...taxonomy.sectors].sort((a, b) => b.value - a.value);
        if (sorted.length <= 5) return sorted;
        const top = sorted.slice(0, 5);
        const other = sorted.slice(5).reduce((acc, curr) => acc + curr.value, 0);
        return [...top, { name: 'Autre', value: other }];
    };

    const sectorData = getSectorData();
    const totalSectorValue = sectorData.reduce((acc, curr) => acc + curr.value, 0);

    const cashValue = (summary?.total_value || 0) - (summary?.total_invested || 0);
    const cashPercentage = summary?.total_value > 0 ? (cashValue / summary.total_value) * 100 : 0;
    const investedPercentage = summary?.total_value > 0 ? (summary.total_invested / summary.total_value) * 100 : 0;
    const historicalPerf = summary?.total_invested > 0 ? ((summary.total_pl / summary.total_invested) * 100) : 0;

    if (loading && !summary) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center pt-24 text-slate-500">
                <div className="relative w-20 h-20 mb-6">
                    <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                    <Layers size={24} className="absolute inset-0 m-auto text-indigo-600 animate-pulse" />
                </div>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight">{t('dashboard.sync_title', 'Synchronisation...')}</h2>
                <p className="text-sm font-medium mt-2 text-slate-400">{t('dashboard.sync_desc', 'Calcul de vos métriques de portefeuille en cours.')}</p>
            </div>
        );
    }

    const monthName = new Date().toLocaleString(i18n.language, { month: 'long' });

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 text-slate-900 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* EN-TÊTE DASHBOARD */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 animate-in slide-in-from-top-4 duration-500">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-slate-200 rounded-full text-xs font-bold text-slate-500 mb-3 shadow-sm">
                            <Clock size={12} className="text-indigo-500"/> {t('dashboard.last_update')} {new Date().toLocaleTimeString(i18n.language, {hour: '2-digit', minute:'2-digit'})}
                        </div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                            {greeting}, <span className="text-indigo-600">{userName || t('dashboard.investor')}</span>
                        </h1>
                        <p className="text-slate-500 font-medium mt-1">{t('dashboard.subtitle')}</p>
                    </div>
                    
                    <div className="flex items-center gap-3 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm w-full md:w-auto">
                        <div className="bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-200 flex-1 md:flex-none flex items-center min-w-[200px]">
                            <Wallet size={16} className="text-slate-400 mr-2"/>
                            <select 
                                className="bg-transparent font-bold text-slate-800 outline-none w-full cursor-pointer appearance-none relative"
                                value={selectedPortfolio || ''} 
                                onChange={e => setSelectedPortfolio(e.target.value)}
                            >
                                {portfolios.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <button onClick={loadData} disabled={loading} className="p-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl transition-colors shrink-0">
                            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
                        </button>
                    </div>
                </div>

                {/* 4 CARTES KPI HAUT DE PAGE */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 relative overflow-hidden group">
                        <div className="absolute -right-6 -top-6 w-32 h-32 bg-indigo-50 rounded-full group-hover:scale-150 transition-transform duration-700 ease-out opacity-50"></div>
                        <div className="relative z-10">
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><Wallet size={14}/> {t('dashboard.net_value')}</span>
                            </div>
                            <div className="text-3xl font-black text-slate-900 font-mono tracking-tight mb-4">{formatMoney(summary?.total_value, summary?.currency)}</div>
                            <div className="space-y-1.5 mt-auto">
                                <div className="flex justify-between text-[10px] font-bold text-slate-500">
                                    <span>{t('dashboard.invested')} {investedPercentage.toFixed(0)}%</span>
                                    <span>{t('dashboard.cash')} {cashPercentage.toFixed(0)}%</span>
                                </div>
                                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden flex">
                                    <div className="h-full bg-indigo-600" style={{ width: `${investedPercentage}%` }}></div>
                                    <div className="h-full bg-emerald-400" style={{ width: `${cashPercentage}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 relative overflow-hidden group">
                        <div className={`absolute -right-6 -top-6 w-32 h-32 rounded-full group-hover:scale-150 transition-transform duration-700 ease-out opacity-50 ${summary?.total_pl >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}></div>
                        <div className="relative z-10 flex flex-col h-full">
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><Activity size={14}/> {t('dashboard.unrealized_pnl')}</span>
                            </div>
                            <div className={`text-3xl font-black font-mono tracking-tight mb-4 ${summary?.total_pl >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {summary?.total_pl > 0 ? '+' : ''}{formatMoney(summary?.total_pl, summary?.currency)}
                            </div>
                            <div className={`mt-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border w-fit font-bold text-xs ${summary?.total_pl >= 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'}`}>
                                {summary?.total_pl >= 0 ? <ArrowUpRight size={14} strokeWidth={2.5}/> : <TrendingDown size={14} strokeWidth={2.5}/>}
                                {historicalPerf > 0 ? '+' : ''}{historicalPerf.toFixed(2)}% {t('dashboard.on_invested')}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 relative overflow-hidden group">
                        <div className="absolute -right-6 -top-6 w-32 h-32 bg-blue-50 rounded-full group-hover:scale-150 transition-transform duration-700 ease-out opacity-50"></div>
                        <div className="relative z-10 flex flex-col h-full">
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><Target size={14}/> {t('dashboard.annual_income')}</span>
                            </div>
                            <div className="text-3xl font-black text-slate-900 font-mono tracking-tight mb-4">{formatMoney(projection?.annual_estimated, summary?.currency)}</div>
                            <div className="mt-auto flex items-center gap-2 text-xs font-bold text-slate-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 w-fit">
                                <ShieldCheck size={14} className="text-blue-500"/>
                                {t('dashboard.yield')}: {summary?.total_value > 0 ? ((projection?.annual_estimated / summary.total_value) * 100).toFixed(2) : 0}%
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-3xl border border-slate-700 shadow-lg shadow-slate-900/20 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden group">
                        <div className="absolute -right-10 -top-10 w-40 h-40 bg-amber-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700 ease-out"></div>
                        <div className="relative z-10 flex flex-col h-full">
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-xs font-bold text-slate-300 uppercase tracking-widest flex items-center gap-2"><Calendar size={14} className="text-amber-400"/> {t('dashboard.this_month')}</span>
                            </div>
                            <div className="text-3xl font-black text-white font-mono tracking-tight mb-4">
                                +{formatMoney(currentMonthDivs, summary?.currency)}
                            </div>
                            <div className="mt-auto text-xs font-medium text-slate-400 flex items-center gap-2 capitalize">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></div>
                                {t('dashboard.collections_of')} {monthName}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ALERTES RADAR */}
                {radarAlerts && radarAlerts.length > 0 && (
                    <div className="bg-slate-900 rounded-3xl border border-indigo-500/30 shadow-lg shadow-indigo-500/10 p-6 mb-8 relative overflow-hidden">
                        <div className="absolute -right-20 -top-20 w-64 h-64 bg-indigo-500/20 rounded-full blur-[80px]"></div>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 relative z-10">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-500/20 text-indigo-400 rounded-xl animate-pulse"><BellRing size={18}/></div>
                                <h3 className="font-black text-white text-sm uppercase tracking-wider">Alertes Tactiques</h3>
                            </div>
                            <span className="bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-full">{radarAlerts.length} Action{radarAlerts.length > 1 ? 's' : ''} signalée{radarAlerts.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex flex-wrap gap-4 relative z-10">
                            {radarAlerts.map(a => {
                                const isOversold = a.rsi < 30;
                                return (
                                    <div key={a.symbol} onClick={() => openStockModal(a.symbol)} className="flex items-center gap-4 bg-slate-800/60 px-4 py-3 rounded-2xl border border-slate-700 hover:bg-slate-700 hover:border-indigo-500/50 transition-all cursor-pointer shadow-sm group">
                                        <div className="font-black text-white text-lg group-hover:text-indigo-400 transition-colors">{a.symbol}</div>
                                        <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${isOversold ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
                                            {isOversold ? 'Survente' : 'Surchauffe'}
                                        </div>
                                        <div className="w-px h-6 bg-slate-700 mx-1"></div>
                                        <div className={`font-mono font-black text-sm ${isOversold ? 'text-emerald-400' : 'text-rose-400'}`}>RSI {a.rsi?.toFixed(0)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* BLOC CENTRAL : SECTEURS & WIDGETS */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 items-start">
                    
                    {/* Analyse Sectorielle (2/3) */}
                    <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 shadow-sm p-8 flex flex-col h-full min-h-[400px]">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="p-2.5 bg-slate-50 text-slate-600 rounded-xl shadow-inner border border-slate-100">
                                <PieIcon size={20}/>
                            </div>
                            <h3 className="font-black text-slate-800 text-lg tracking-tight">
                                {t('dashboard.sector_allocation')}
                            </h3>
                        </div>
                        <div className="flex flex-col md:flex-row gap-10 flex-1 items-center justify-center">
                            <div className="h-64 w-full md:w-1/2 relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={sectorData} cx="50%" cy="50%" innerRadius={85} outerRadius={120} paddingAngle={3} dataKey="value" stroke="none">
                                            {sectorData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                                        </Pie>
                                        <RechartsTooltip contentStyle={{borderRadius:'16px', border:'none', boxShadow:'0 10px 25px -5px rgba(0,0,0,0.1)'}} formatter={(val) => `${val.toFixed(0)}`} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <span className="text-4xl font-black text-slate-900">{sectorData.length}</span>
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('dashboard.sectors')}</span>
                                </div>
                            </div>
                            <div className="w-full md:w-1/2 space-y-4">
                                {sectorData.map((s, idx) => {
                                    const pct = totalSectorValue > 0 ? (s.value / totalSectorValue) * 100 : 0;
                                    return (
                                        <div key={s.name} className="flex items-center justify-between group">
                                            <div className="flex items-center gap-3 w-1/2">
                                                <div className="w-4 h-4 rounded-full shrink-0 shadow-sm" style={{backgroundColor: COLORS[idx % COLORS.length]}}></div>
                                                <div className="text-sm font-bold text-slate-700 truncate group-hover:text-slate-900 transition-colors" title={s.name}>{s.name}</div>
                                            </div>
                                            <div className="flex items-center gap-4 w-1/2 justify-end">
                                                <div className="text-sm font-black text-slate-900">{pct.toFixed(1)}%</div>
                                                <div className="w-20 h-2.5 bg-slate-100 rounded-full overflow-hidden shrink-0 shadow-inner">
                                                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: COLORS[idx % COLORS.length] }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Widgets (1/3) */}
                    <div className="lg:col-span-1 flex flex-col gap-6 h-full">
                        <TridentOpportunityWidget />
                        <EarningsCalendarWidget earnings={earningsData} />
                    </div>
                </div>

                {/* 3 COLONNES BOTTOM : POIDS LOURDS / TOPS / FLOPS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                        <div className="bg-slate-50 border-b border-slate-100 p-5 flex items-center justify-between">
                            <h3 className="font-black text-slate-800 flex items-center gap-2 text-sm uppercase tracking-wider">
                                <Award size={16} className="text-indigo-600"/> {t('dashboard.heavyweights', 'Poids Lourds')}
                            </h3>
                        </div>
                        <div className="p-2 space-y-1">
                            {topWeight.map((p, i) => (
                                <div key={p.symbol} onClick={() => openStockModal(p.symbol)} className="flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer group border border-transparent hover:border-slate-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-black text-slate-700 shadow-sm group-hover:border-indigo-200 group-hover:text-indigo-600 transition-colors">
                                            {p.symbol.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-sm text-slate-900 leading-none mb-1">{p.symbol}</div>
                                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider truncate w-24">{p.name}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-black text-sm text-slate-800">{p.weight?.toFixed(1)}%</div>
                                        <div className="text-[11px] text-slate-400 font-bold font-mono">{formatMoney(p.value_converted, summary?.currency)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                        <div className="bg-emerald-50 border-b border-emerald-100/60 p-5 flex items-center justify-between">
                            <h3 className="font-black text-emerald-900 flex items-center gap-2 text-sm uppercase tracking-wider">
                                <ArrowUpRight size={16} className="text-emerald-600"/> {t('dashboard.top_performers', 'Top Performers')}
                            </h3>
                        </div>
                        <div className="p-2 space-y-1">
                            {bestPerf.map((p) => (
                                <div key={p.symbol} onClick={() => openStockModal(p.symbol)} className="flex items-center justify-between p-3 rounded-2xl hover:bg-emerald-50/50 transition-colors cursor-pointer group border border-transparent hover:border-emerald-100/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-black text-slate-600 shadow-sm group-hover:border-emerald-200 group-hover:text-emerald-600 transition-colors">
                                            {p.symbol.charAt(0)}
                                        </div>
                                        <div className="font-black text-sm text-slate-900">{p.symbol}</div>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-emerald-700 font-black text-sm bg-emerald-100/50 px-3 py-1.5 rounded-xl border border-emerald-200/50 group-hover:bg-emerald-100 transition-colors font-mono">
                                        +{p.gain_p?.toFixed(2)}%
                                    </div>
                                </div>
                            ))}
                            {bestPerf.length === 0 && <div className="p-8 text-center text-slate-400 text-sm font-medium">{t('dashboard.no_gains')}</div>}
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                        <div className="bg-rose-50 border-b border-rose-100/60 p-5 flex items-center justify-between">
                            <h3 className="font-black text-rose-900 flex items-center gap-2 text-sm uppercase tracking-wider">
                                <ArrowDownRight size={16} className="text-rose-600"/> {t('dashboard.under_watch', 'Sous Surveillance')}
                            </h3>
                        </div>
                        <div className="p-2 space-y-1">
                            {worstPerf.map((p) => (
                                <div key={p.symbol} onClick={() => openStockModal(p.symbol)} className="flex items-center justify-between p-3 rounded-2xl hover:bg-rose-50/50 transition-colors cursor-pointer group border border-transparent hover:border-rose-100/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-black text-slate-600 shadow-sm group-hover:border-rose-200 group-hover:text-rose-600 transition-colors">
                                            {p.symbol.charAt(0)}
                                        </div>
                                        <div className="font-black text-sm text-slate-900">{p.symbol}</div>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-rose-700 font-black text-sm bg-rose-100/50 px-3 py-1.5 rounded-xl border border-rose-200/50 group-hover:bg-rose-100 transition-colors font-mono">
                                        {p.gain_p?.toFixed(2)}%
                                    </div>
                                </div>
                            ))}
                            {worstPerf.length === 0 && <div className="p-8 text-center text-slate-400 text-sm font-medium flex flex-col items-center gap-2"><Zap size={24} className="text-emerald-400"/> {t('dashboard.no_losses')}</div>}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}
